import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Maximize2 } from "lucide-react";
import AthleteBadges from "@/components/AthleteBadges";
import { CompetitionLogo } from "@/components/CompetitionLogo";

const MEDAL = ["#FFCC00", "#C0C0C0", "#CD7F32"];

export default function Telao() {
  const { competitionId, categoryId } = useParams();
  const [comps, setComps] = useState([]);
  const [cats, setCats] = useState([]);
  const [compId, setCompId] = useState(competitionId || "");
  const [catId, setCatId] = useState(categoryId || "");
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/competitions").then((r) => {
      setComps(r.data);
      if (!compId && r.data[0]) setCompId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!compId) return;
    api.get("/public/categories", { params: { competition_id: compId } }).then((r) => {
      setCats(r.data);
      if (!catId && r.data[0]) setCatId(r.data[0].id);
    });
  }, [compId]);

  useEffect(() => {
    if (!compId || !catId) return;
    const fetchData = () =>
      api
        .get("/public/ranking", { params: { competition_id: compId, category_id: catId } })
        .then((r) => setData(r.data));
    fetchData();
    const t = setInterval(fetchData, 5000);
    return () => clearInterval(t);
  }, [compId, catId]);

  useEffect(() => {
    if (compId && catId) {
      window.history.replaceState(null, "", `/telao/${compId}/${catId}`);
    }
  }, [compId, catId]);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  const currentCat = cats.find((c) => c.id === catId);
  const currentComp = comps.find((c) => c.id === compId);
  const podium = data?.ranking?.slice(0, 3) || [];
  const rest = data?.ranking?.slice(3, 10) || [];

  return (
    <div className="min-h-screen text-white" style={{ background: "linear-gradient(180deg, #0A0A0A 0%, #111111 100%)" }}>
      {/* Header controls (only visible when not fullscreen) */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3 bg-black/80 backdrop-blur border-b border-[#C9A84C]/25">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#C9A84C] text-[#0A0A0A] rounded-sm">
            <Trophy size={16} strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-lg leading-none">FITS4YOU EVENTS · TELÃO</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <select
            data-testid="telao-comp"
            className="select-field"
            value={compId}
            onChange={(e) => setCompId(e.target.value)}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <select
            data-testid="telao-cat"
            className="select-field"
            value={catId}
            onChange={(e) => setCatId(e.target.value)}
          >
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <button className="btn-ghost flex items-center gap-1" onClick={goFullscreen}>
            <Maximize2 size={14} /> Tela cheia
          </button>
          <button className="btn-ghost" onClick={() => nav("/")}>Sair</button>
        </div>
      </div>

      <div className="pt-20 px-12 pb-10 min-h-screen">
        <div className="mb-8 text-center">
          <div className="flex justify-center" data-testid="telao-comp-header">
            <CompetitionLogo
              competition={currentComp}
              height={100}
              testId="telao-comp-logo"
              fallback={
                <div className="overline text-lg tracking-[0.3em]" data-testid="telao-comp-name">
                  {currentComp?.nome}
                </div>
              }
            />
          </div>
          <div className="font-display text-7xl md:text-8xl font-black tracking-tight mt-2 leading-none">
            {currentCat?.nome || "—"}
          </div>
        </div>

        {/* Podium */}
        {podium.length > 0 && (
          <div className="grid grid-cols-3 gap-6 mb-10">
            {[1, 0, 2].map((idx) => {
              const p = podium[idx];
              if (!p) return <div key={idx} />;
              const height = idx === 0 ? "min-h-[400px]" : idx === 1 ? "min-h-[500px]" : "min-h-[340px]";
              return (
                <div
                  key={idx}
                  className={`card ${height} flex flex-col justify-end p-8 border-4`}
                  style={{ borderColor: p.posicao ? MEDAL[idx] : "transparent" }}
                >
                  <div
                    className="font-display font-black leading-none"
                    style={{ color: MEDAL[idx], fontSize: idx === 0 ? "8rem" : "6rem" }}
                  >
                    {p.posicao ? `${p.posicao}º` : "—"}
                  </div>
                  <div className="mt-4">
                    <div className="text-2xl text-[var(--text-secondary)] tabular-nums">
                      #{p.numero}
                    </div>
                    <div
                      className="font-display font-black tracking-tight leading-none mt-1 flex items-center flex-wrap gap-2"
                      style={{ fontSize: idx === 0 ? "3.5rem" : "2.5rem" }}
                    >
                      {p.nome || "—"}
                      <AthleteBadges athlete={{ ...p, id: p.athlete_id }} size={idx === 0 ? 24 : 20} />
                    </div>
                    <div className="text-xl text-[var(--text-secondary)] mt-2">
                      {p.equipe || "—"}
                    </div>
                    <div className="overline mt-4">Total</div>
                    <div className="font-display font-bold text-4xl">
                      {p.score_final ?? "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rest */}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rest.map((r) => (
              <div key={r.posicao} className="card px-6 py-4 flex items-center gap-6">
                <div className="font-display font-black text-4xl tabular-nums w-16">
                  {r.posicao ? `${r.posicao}º` : "—"}
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-2xl tracking-tight leading-none flex items-center flex-wrap gap-2">
                    {r.nome}
                    <AthleteBadges athlete={{ ...r, id: r.athlete_id }} size={16} />
                  </div>
                  <div className="text-sm text-[var(--text-secondary)]">
                    #{r.numero} · {r.equipe || "—"}
                  </div>
                </div>
                <div className="font-display font-bold text-3xl tabular-nums">
                  {r.score_final ?? "—"}
                </div>
              </div>
            ))}
          </div>
        )}

        {(!data || data.ranking.length === 0) && (
          <div className="text-center py-20 text-3xl font-display text-[var(--text-secondary)]">
            Aguardando resultados...
          </div>
        )}
      </div>
    </div>
  );
}
